// E25-t3 — THE DASHBOARD SWITCHER, in the bar of the dashboard altitude.
//
// Rebuilt on DropdownMenu (2026-08-07 design audit): the design draws this as
// a menu of named places — the scope pickers' own grammar (AppShell) — not a
// form field, so the Selector it wore is retired the way the org picker's was.
// The trigger names the dashboard the reader is ON; the popover lists every
// published dashboard with its render facts, and its footer is the door back
// to the KB's Dashboards page plus the stable read-only render, re-homed here
// from the page header the audit removed (the artifact owns every pixel under
// the bar — see DashboardView.tsx).
import { useEffect, useState } from 'react';

import { Divider } from '@astryxdesign/core/Divider';
import { DropdownMenu, DropdownMenuItem } from '@astryxdesign/core/DropdownMenu';
import { Link } from '@astryxdesign/core/Link';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { Stack } from '@astryxdesign/core/Stack';
import { StatusDot } from '@astryxdesign/core/StatusDot';
import { Text } from '@astryxdesign/core/Text';

import type { DashboardsClient } from '../api/dashboards';
import { relShort } from '../format/units';
import { stableDashboardUrl } from '../pages/DashboardView';

export interface DashboardSwitcherProps {
  org: string;
  kb: string;
  /** the format the reader is ON — the route's own segment */
  format: string;
  client: DashboardsClient;
  onNavigate: (href: string) => void;
  /** the resolved CURRENT title, handed up so the brand menu can name the
   *  dashboard when the bar hides this control (mobile — Felipe's verdict,
   *  2026-08-07: the name lives in the menu there) */
  onTitle?: (title: string) => void;
  /** SERVE_ROUTE for the format the reader is ON. Defaults to the one place
   *  the url shape is spelled (stableDashboardUrl), overridable by the
   *  container should the serving route ever move. */
  stableUrl?: string;
}

/** One row of the popover: a format's first composition, with its render facts
 *  (title/commit/renderedAt are the manifest's — src/api/dashboards.ts). */
interface Entry {
  format: string;
  title: string;
  commit: string | null;
  renderedAt: string | null;
  /** E33-t4 — the format's REPORT surface url (the catalog's own string,
   *  src/api/dashboards.ts), or null when the format owns none. The switcher
   *  is the dashboard altitude's chrome — the artifact owns every pixel under
   *  the bar — so this is where the family's other surface gets its door. */
  reportUrl: string | null;
}

/** The rail's four-tile dashboards glyph (AppShell navIcon), at bar size. */
function DashGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 4.5h6.5V11H4z" />
      <path d="M13.5 4.5H20V11h-6.5z" />
      <path d="M4 13h6.5v6.5H4z" />
      <path d="M13.5 13H20v6.5h-6.5z" />
    </svg>
  );
}

/** The current-entry check. Its absent twin is a 15px spacer so titles align. */
function CheckGlyph() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function DashboardSwitcher({
  org,
  kb,
  format,
  client,
  onNavigate,
  onTitle,
  stableUrl = stableDashboardUrl(org, kb, format),
}: DashboardSwitcherProps) {
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setEntries(null);
    setError(null);
    void client
      .listDashboards(org, kb)
      .then((manifest) => {
        if (!live) return;
        setEntries(
          manifest.formats.map((entry) => ({
            format: entry.format,
            title: entry.compositions[0]?.title || entry.format,
            commit: entry.compositions[0]?.commit ?? null,
            renderedAt: entry.compositions[0]?.renderedAt ?? null,
            reportUrl: entry.report?.url ?? null,
          })),
        );
      })
      .catch((err: Error) => {
        // The refusal travels in the guard's own words ('<guard>: <reason>',
        // src/api/dashboards.ts) — stored, not swallowed: the popover shows it.
        if (live) setError(err.message);
      });
    return () => {
      live = false;
    };
  }, [org, kb, client]);

  const current = entries?.find((entry) => entry.format === format) ?? null;
  // The manifest failing must not cost the bar a control that names where the
  // reader IS: the trigger degrades to the route's own segment.
  const currentTitle = current?.title ?? format;

  useEffect(() => {
    onTitle?.(currentTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTitle]);

  // ONE dashboard is a fact, not a choice: a static line saying where you are.
  // No disabled Selector and no disabledMessage — that grammar is recorded as
  // retired with the Selector itself (AppShell: "a menu of one org simply
  // shows the one org"), and a menu of one entry would be a control that does
  // nothing. 2026-08-07 design audit.
  if (entries !== null && entries.length <= 1) {
    const solo =
      entries[0] ?? { format, title: format, commit: null, renderedAt: null, reportUrl: null };
    return (
      <Stack
        direction="horizontal"
        gap={2}
        vAlign="center"
        className="dj-solo-dash"
        data-testid="dashboard-switcher"
      >
        <DashGlyph />
        <Text type="label" weight="semibold">
          {solo.title}
        </Text>
        <Text type="code" color="secondary">
          {solo.format}
        </Text>
        {/* E33-t4 — the solo face keeps the family's other door: a real anchor
            (the report is a served document of its own, outside the shell),
            not a button — the solo face stays a fact, not a control. */}
        {solo.reportUrl ? (
          <Link href={solo.reportUrl} type="supporting">
            report
          </Link>
        ) : null}
      </Stack>
    );
  }

  return (
    <span data-testid="dashboard-switcher" style={{ display: 'contents' }}>
      <DropdownMenu
        hasChevron
        menuWidth={372}
        button={{
          label: currentTitle,
          variant: 'ghost',
          size: 'sm',
          icon: <DashGlyph />,
        }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          hAlign="between"
          vAlign="center"
          style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}
        >
          <Text type="supporting">PUBLISHED DASHBOARDS</Text>
          {entries !== null ? <Text type="code">{`${entries.length} in ${kb}`}</Text> : null}
        </Stack>

        {error !== null ? (
          <Stack gap={1} style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
            <Stack direction="horizontal" gap={2} vAlign="center">
              <StatusDot variant="error" label="Dashboard index refused" />
              <Text>Dashboard index refused</Text>
            </Stack>
            <Text type="code" color="secondary">
              {error}
            </Text>
          </Stack>
        ) : entries === null ? (
          [0, 1, 2].map((row) => (
            <Stack
              key={row}
              gap={1}
              style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}
            >
              <Skeleton width={`${58 + ((row * 17) % 24)}%`} height="12px" />
              <Skeleton width="42%" height="10px" />
            </Stack>
          ))
        ) : (
          entries.map((entry) => {
            const facts = [entry.commit, entry.renderedAt ? relShort(entry.renderedAt) : null]
              .filter((fact): fact is string => fact !== null)
              .join(' · ');
            return (
              <DropdownMenuItem
                key={entry.format}
                icon={
                  entry.format === format ? (
                    <CheckGlyph />
                  ) : (
                    <span style={{ display: 'inline-block', width: 15 }} />
                  )
                }
                label={
                  <>
                    <Text type="label" weight="semibold">
                      {entry.title}
                    </Text>{' '}
                    <Text type="code" color="secondary">
                      {entry.format}
                    </Text>
                  </>
                }
                description={facts ? `RENDERED ${facts}` : undefined}
                endContent={
                  /* E33-t4 — the format's report surface, opened from its own
                     row: a real anchor (the artifact is a document outside the
                     shell, like the footer's read-only render), stopPropagation
                     so following it does not also fire the row's navigation. */
                  entry.reportUrl ? (
                    <Link
                      href={entry.reportUrl}
                      type="supporting"
                      onClick={(e) => e.stopPropagation()}
                    >
                      report
                    </Link>
                  ) : undefined
                }
                onClick={() =>
                  onNavigate(`/${org}/${kb}/dashboards/${encodeURIComponent(entry.format)}`)
                }
              />
            );
          })
        )}

        <Divider />
        <DropdownMenuItem
          label={`Dashboards in ${kb}`}
          onClick={() => onNavigate(`/${org}/${kb}/dashboards`)}
          endContent={
            /* The stable url is a shareable address, so it is offered as one —
               re-homed from the DashboardView header (2026-08-07 design
               audit). A real anchor on purpose: the artifact is a document of
               its own, outside the shell. stopPropagation so following it does
               not also fire the row's in-app navigation. */
            <Link href={stableUrl} type="supporting" onClick={(e) => e.stopPropagation()}>
              read-only render
            </Link>
          }
        />
      </DropdownMenu>
    </span>
  );
}
